export interface CaseStudy {
  id: string;
  slug: string;
  client: string;
  industry: string;
  results: string;
  timeline: string;
  date: string;
  category: string;
  tags: string[];
  published: boolean;
  title: Record<string, string>;
  subtitle: Record<string, string>;
  excerpt: Record<string, string>;
  challenge: Record<string, string>;
  solution: Record<string, string>;
  outcome: Record<string, string>;
  featuredImage: string;
}

export const SEED_CASES: CaseStudy[] = [
  {
    id: "case-1",
    slug: "roomy-linear-workspaces",
    client: "Linear Corporate Inc",
    industry: "PropTech & Real Estate",
    results: "+42% Space Utilisation",
    timeline: "4 Weeks Delivery",
    date: "2026-05-12",
    category: "PropTech",
    tags: ["Workspace Monitoring", "Predictive Booking", "Sensory Interaction"],
    published: true,
    title: {
      en: "Revolutionising Office Resource Allocation for Linear Corporate",
      es: "Revolucionando la asignación de recursos de oficina para Linear Corporate",
      fr: "Révolutionner l'allocation des ressources de bureau chez Linear Corporate",
      de: "Revolutionierung der Büroressourcen-Allokation für Linear Corporate",
      hi: "लीनियर कॉर्पोरेट के लिए कार्यालय संसाधन आवंटन में क्रांति",
      zh: "为 Linear 集团打造革命性智能空间协同方案",
      ja: "Linear Corporate におけるオフィスリソース割り当ての革新",
      ar: "ثورة في تخصيص موارد المكاتب لشركة لينير كليوباترا"
    },
    subtitle: {
      en: "How Roomy's 150ms micro-feedbacks and real-time spatial engine optimized hybrid desk booking.",
      es: "Cómo los microcompartidos de 150ms de Roomy optimizaron la reserva de escritorios híbridos.",
      fr: "Comment les micro-retours de 150ms de Roomy ont optimisé la réservation de bureaux hybrides.",
      de: "Wie die 150ms-Mikrofeedbacks von Roomy die hybride Schreibtischbuchung optimierten.",
      hi: "कैसे रूमी के 150ms सूक्ष्म-प्रतिक्रिया ने हाइब्रिड डेस्क बुकिंग को अनुकूलित किया।",
      zh: "通过 Roomy 级联式 150 毫秒微反馈和实时空间状态机，轻松化解混合办公预订冲突。",
      ja: "Roomyの150msマイクロフィードバックとリアルタイム空間エンジンがハイブリッドデスク予約を最適化した方法。",
      ar: "كيف ساهمت لوحات استجابة Roomy بمعدل 150 ملي ثانية في تسهيل الحجز المشترك."
    },
    excerpt: {
      en: "Empowering 12,000+ engineers at Linear with automated sensory hot-desking, bringing space leakages down to perfect absolute zero.",
      es: "Capacitando a más de 12,000 ingenieros en Linear con reserva inteligente, reduciendo el desperdicio de espacio a cero absoluto.",
      fr: "Permettre à plus de 12 000 ingénieurs chez Linear de réserver intelligemment, réduisant la perte d'espace à zéro absolu.",
      de: "Unterstützung von mehr als 12.000 Ingenieuren bei Linear mit intelligenter Schreibtischbuchung, wodurch Leerstände vollständig eliminiert wurden.",
      hi: "लीनियर में 12,000 से अधिक इंजीनियरों को स्वचालित संवेदी हॉट-डेस्किंग के साथ सशक्त बनाना।",
      zh: "运用模块化极速空间分配器，助力 Linear 超过 12,000 名研发人员实现无缝无冲突的热工位动态分配。",
      ja: "Linearの12,000人以上のエンジニアに感覚的な自動ホットデスク環境を提供し、デッドスペースを完全にゼロ化。",
      ar: "تمكين أكثر من 12000 مهندس من الاستغلال الذكي للمساحات السحابية الشاغرة."
    },
    challenge: {
      en: "Linear Inc struggled with highly fragmented desk booking files, slow and double-booked hot desk calendars, and massive space leakages where empty desks remained locked while engineers struggled to find visual workspace plans in active offices.",
      es: "Linear Inc luchaba con archivos fragmentados, reservas duplicadas en calendarios lentos y un gran desperdicio de espacios libres que figuraban como ocupados.",
      fr: "Linear Inc luttait contre des fichiers fragmentés, des réservations doublées sur des calendriers lents et un gaspillage important d'espaces pourtant libres.",
      de: "Linear Inc hatte mit stark fragmentierten Buchungsdateien, trägen und doppelt gebuchten Hot-Desk-Kalendern sowie massiven Flächenverlusten zu kämpfen.",
      hi: "लीनियर इंक को अत्यधिक विखंडित डेस्क बुकिंग फाइलों, धीमी और दोहरी बुकिंग समस्या का सामना करना पड़ा और भारी नुकसान उठाना पड़ रहा था।",
      zh: "Linear 公司常年面临庞杂琐碎的工位表格冲突、加载极其缓慢的旧式预订日历，以及工位空置率高居不下却有大批员工反馈订不到位置的痛点。",
      ja: "Linear社では、細分化された予約ファイル、低速で二重予約が発生するカレンダー、未使用のデスクがロックされたまま放置されるなど、大きな機会損失が発生していました。",
      ar: "عانت شركة لينير من حجز الملفات المجزأة، وتصادم مواعيد المكاتب المزدوجة."
    },
    solution: {
      en: "We integrated Roomy™: an elite desktop-first single-view spatial allocation dashboard. Featuring real-time room occupancies, instant websocket synchronization state cues, custom high-contrast graphic matrices, and dynamic lazy-loaded SVG visual assets keeping Core Web Vitals scoreboards green.",
      es: "Integramos Roomy™: un panel de asignación espacial minimalista y rápido. Con estado de ocupación en tiempo real y activos vectoriales vectoriales de carga perezosa.",
      fr: "Nous avons intégré Roomy™ : un tableau de bord spatial minimaliste et rapide. Avec état d'occupation en temps réel et fichiers vectoriels paresseux.",
      de: "Wir haben Roomy™ integriert: Ein erstklassiges, performantes Dashboard zur räumlichen Allokation. Ausgestattet mit Echtzeit-Raumbelegung und träge ladenden SVG-Grafiken.",
      hi: "हमने रूमी™ को एकीकृत किया: एक विशिष्ट डेस्कटॉप-प्रथम एकल-दृश्य स्थानिक आवंटन डैशबोर्ड, जो वास्तविक समय अधिभोग और सुचारू एनीमेशन युक्त है।",
      zh: "为此我们部署了专属的 Roomy™ 引擎：一套单视图、低延迟的空间分配控制面板。采用极致轻量化的 Lottie 与惰性化 SVG 向量绘制技术，确保全天候核心页面指标获得满分体验。",
      ja: "そこでRoomy™を導入。リアルタイムの座席占有度表示、遅延読み込み型SVGアセット、そして高速なデータ同期状態を誇る美しいシングルビューコントロール画面を提供しました。",
      ar: "قمنا بدمج نظام Roomy™ الذكي: لوحة تحكم متكاملة تدعم تتبع الحيز المكاني بنقرة واحدة."
    },
    outcome: {
      en: "Linear achieved a +42% efficiency boost on existing office leases. Underwriting and double-bookings reduced to exactly absolute 0%, resulting in over $450,000 per year saved across global engineering spaces.",
      es: "Linear logró un aumento del +42% de eficiencia en contratos de oficina. Reservas duplicadas nulas, ahorrando más de $450,050 anuales en espacios de desarrollo.",
      fr: "Linear a réalisé un gain de +42% d'efficacité sur ses baux. Zéro réservation doublée, économisant plus de 450 000 $ par an sur les espaces de développement.",
      de: "Linear erreichte eine Effizienzsteigerung der Büroflächen um +42%. Doppelbuchungen wurden auf 0% reduziert, was jährliche Einsparungen von über 450.000 $ ermöglichte.",
      hi: "लीनियर ने मौजूदा कार्यालय पट्टों पर +42% दक्षता वृद्धि हासिल की। दोहरी बुकिंग 0% हुई, जिससे दुनिया भर में प्रति वर्ष $450,000 से अधिक की बचत हुई।",
      zh: "极大地盘活了现有办公室资产，综合空间利用效率飙升 42%。多订、错订事件化解至 0%，为其全球办公室累计削减年度租金开销达 450,000 美元。",
      ja: "オフィスの実質利用率が +42% 向上。二重予約は完全にゼロとなり、グローバル拠点を合わせ年間45万ドル以上の管理コスト削減に成功しました。",
      ar: "حققت الشركة زيادة إنتاجية ملحوظة بنسبة 42% مع تنظيم كامل لسير العمل."
    },
    featuredImage: "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=800&q=80"
  },
  {
    id: "case-2",
    slug: "siteforces-vertex-analytics",
    client: "Vertex Build & Logistics",
    industry: "SaaS Construction Management",
    results: "$140,000 Yearly Savings",
    timeline: "6 Weeks Delivery",
    date: "2026-04-20",
    category: "SaaS",
    tags: ["SiteForces Studio", "Workforce Analytics", "Live Attendance", "QR Systems"],
    published: true,
    title: {
      en: "Eliminating Administrative Paper Trails at Vertex Build",
      es: "Eliminando pistas de papel administrativo en Vertex Build",
      fr: "Élimination de la paperasse chez Vertex Build",
      de: "Eliminierung von administrativem Papierkram bei Vertex Build",
      hi: "वर्टेक्स बिल्ड में प्रशासनिक कागजी पगडंडियों को समाप्त करना",
      zh: "Vertex 建筑工程实现 100% 数字化考勤与实时薪资看板",
      ja: "Vertex Buildにおける管理ペーパープロセスの完全撤廃",
      ar: "القضاء على المعاملات الورقية الإدارية في شركة فيرتكس يلدز"
    },
    subtitle: {
      en: "Deploying SiteForces smart QR attendance systems and active wage calculation ticker hooks.",
      es: "Implementación de sistemas QR inteligentes y contadores activos de salarios.",
      fr: "Déploiement des systèmes QR intelligents et compteurs actifs de salaires.",
      de: "Einführung des intelligenten QR-Anwesenheitssystems und des Live-Lohn-Tickers.",
      hi: "साइटफोर्सेस स्मार्ट क्यूआर उपस्थिति प्रणाली और लाइव वेतन गणना टिकर हुक को तैनात करना।",
      zh: "集成 SiteForces 建筑工地智能 QR 图腾，结合毫秒级递增薪资累加组件，大幅度削减手工开销。",
      ja: "SiteForces スマートQRコード打刻システムとリアルタイム賃金インクリメントカウンターの統合。",
      ar: "نشر أنظمة تتبع الحضور الذكية القائمة على تشفير QR لربط الأجور تلقائيًا."
    },
    excerpt: {
      en: "Vertex successfully digitized 45+ remote building blueprints with interactive workforce maps, achieving unprecedented payroll transparency.",
      es: "Vertex digitalizó con éxito más de 45 planos de construcción con mapas de mano de obra interactivos.",
      fr: "Vertex a numérisé avec succès plus de 45 plans de chantier avec cartes d'effectifs en temps réel.",
      de: "Vertex digitalisierte erfolgreich über 45 Rohbauten mit interaktiven Belegschaftskarten.",
      hi: "वर्टेक्स ने इंटरैक्टिव वर्कफ़ोर्स मानचित्रों के साथ 45+ दूरस्थ भवन ब्लूप्रिंट को सफलतापूर्वक डिजिटल किया।",
      zh: "助力 Vertex 团队在 45 个异地大型建筑平面图内落地在线工时核算，实现前所未有的工程薪资流水透明化。",
      ja: "45カ所以上の工事現場のリアルタイム人員マップを完全デジタル化し、給与会計プロセスの透明度を100%に達成。",
      ar: "نجحت شركة فيرتكس في تحويل 45 مخطط بناء هجين إلى خرائط تفاعلية متكاملة."
    },
    challenge: {
      en: "Vertex construction supervisors lost dozens of overhead hours manually compiling paper logs, tracking remote workforce location tags, and disputing timesheet mismatches across highly active, uncoordinated brick masonry zones.",
      es: "Los supervisores de Vertex perdían horas conciliando registros manuales de turnos, asumiendo grandes costes por desajustes horarios y falta de coordinación.",
      fr: "Les superviseurs de Vertex perdaient des heures à concilier des feuilles d'heures manuscrites, causant d'importantes erreurs de paye et des retards.",
      de: "Die Bauleiter von Vertex verloren unzählige Stunden mit dem manuellen Abgleich von Stundenzetteln und der mühsamen Koordinierung entlegener Trupps.",
      hi: "वर्टेक्स निर्माण पर्यवेक्षकों ने अत्यधिक सक्रिय क्षेत्रों में समय पत्रक विसंगतियों आदि के कारण दर्जनों घंटे खो दिए।",
      zh: "传统的工程工地多以纸质名册、打卡单据为核心，层层转交导致假账、代打卡现象多发，且财务需要月底手工校算，极易发生人工账目扯皮纠纷。",
      ja: "Vertex社の現場管理者たちは、毎日膨大な時間を紙の手書き日報の集計、別々の現場員配置の突き合わせ、賃金過不足トラブルの確認に浪費していました。",
      ar: "واجه مراقبو البناء في Vertex مصاعب في تجميع السجلات الورقية يدويًا وضياع ساعات المراقبة."
    },
    solution: {
      en: "We integrated SiteForces Construction Hub dashboards. Incorporating lazy-loaded Lottie blueprint schematics, scanning radar HUD animation systems, modular attendance checklists, and reactive math tickers counting collective earned wages.",
      es: "Uso de paneles SiteForces con animaciones de radar Lottie, gráficos ligeros y calculadoras optimizadas.",
      fr: "Utilisation des tableaux de bord SiteForces avec animations radars Lottie ultralégères et calculateurs dynamiques.",
      de: "Wir installierten das SiteForces Construction Dashboard inklusive ladezeitoptimierter Lottie-Baupläne und Echtzeit-Lohnrechner.",
      hi: "हमने साइटफोर्सेस कंस्ट्रक्शन हब डैशबोर्ड को एकीकृत किया, जिसमें न्यूनतम-पेलोड वाले Lottie ब्लूप्रिंट और उपस्थिति सूची शामिल है।",
      zh: "我们部署了 SiteForces 智能考勤与调度中心看板。包含轻量 Lottie 工艺流程矢量动效、动态雷达雷影扫描图层，以及秒级自增的工程累加统计器。",
      ja: "SiteForces 工事ダッシュボードを採用。画面のレンダリングを妨げないインテリジェントLottie設計で動くレーダーHUD、QR勤怠チェッカー、自動賃金計算メーターを導入しました。",
      ar: "توفير حلول SiteForces الذكية للمواقع الهندسية المتعددة."
    },
    outcome: {
      en: "Eliminated fraudulent timesheets down to 0%. Handled over $8.4M in transactional workforce checks, saving Vertex $140,000 annually in billing overheads and admin resources.",
      es: "Disminución de hojas de firmas fraudulentas a cero. Vertex ahorró $140,000 al año en contabilidad y costos administrativos.",
      fr: "Diminution des fraudes à zéro. Vertex a économisé 140 000 $ par an sur la gestion administrative.",
      de: "Betrug beim Zeitnachweis sank auf 0%. Vertex spart nun über 140.000 $ pro Jahr an Abrechnungs- und Administrationsaufwand.",
      hi: "धोखाधड़ी वाली उपस्थिति पत्रक को 0% तक समाप्त किया गया, जिससे वर्टेक्स को सालाना $140,000 की बचत हुई।",
      zh: "考勤漏洞和虚假出勤一举归零。累计监管工程工时流过数百万美元，每年为 Vertex 净省去 140,000 美元的繁杂管理核算费用。",
      ja: "不正打刻請求は0%に。自動コンプライアンス管理が働き、年間14万ドルの事務処理・管理オーバーヘッドコストの削減を果たしました。",
      ar: "تخفيض التلاعب في ساعات الحضور إلى الصفر تمامًا وتوفير 140 ألف دولار سنويًا."
    },
    featuredImage: "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?auto=format&fit=crop&w=800&q=80"
  },
  {
    id: "case-3",
    slug: "arrow-escape-queue-engagement",
    client: "Arcade Labs Entertainment",
    industry: "Retro Gaming & Play",
    results: "+42% Retention Jump",
    timeline: "3 Weeks Delivery",
    date: "2026-03-05",
    category: "Gaming",
    tags: ["Arrow Escape", "Performance Play", "Zero-Canvas SVG Physics", "User Experience"],
    published: true,
    title: {
      en: "Boosting Micro-Leisure Waiting Metrics for Arcade Labs",
      es: "Impulsando métricas de ocio para Arcade Labs",
      fr: "Susciter de l'engagement lors de l'attente avec Arcade Labs",
      de: "Steigerung der Spiele-Verweildauer für Arcade Labs",
      hi: "आर्केड लैब्स के लिए माइक्रो-अवकाश प्रतीक्षा मेट्रिक्स को बढ़ावा देना",
      zh: "运用轻量交互式迷宫引擎，Arcade Labs 用户等待留存提升 42%",
      ja: "Arcade Labsにおける待ち時間のアクティブエンゲージメント最大化",
      ar: "تحفيز معدلات الترفيه الخاطف وألعاب المتصفح لصالح Arcade Labs"
    },
    subtitle: {
      en: "How an elegant 12KB vector labyrinth emulator transformed client queue bottlenecks.",
      es: "Cómo un emulador laberíntico de 12KB transformó tiempos de espera.",
      fr: "Comment un émulateur de labyrinthe de 12KB a transformé l'expérience d'attente.",
      de: "Wie ein eleganter 12-KB-Vektor-Labyrinth-Emulator Wartezeiten verkürzte.",
      hi: "कैसे एक सुरुचिपूर्ण 12KB वेक्टर भूलभुलैया एमुलेटर ने कतारों में लगने वाले समय को बदला।",
      zh: "借助精悍至极的 12KB SVG 向量物理引擎，将死板枯燥的云队列等待页变身为玩家爱不释手的竞技场。",
      ja: "僅か12KBのベクター迷路エミュレータが、サービスの読み込み待ち時間ボトルネックを解決したアプローチ。",
      ar: "كيف ساهم محاكي المتاهة الموجه الصغير بحجم 12 كيلوبايت في تحويل ملل قوائم الانتظار."
    },
    excerpt: {
      en: "Building a fluid canvas-free interactive gameplay element keeping mobile frame rates locked solid at 60 fps without battery exhaustion.",
      es: "Construyendo un juego interactivo libre de canvas que mantiene 60 fps en móviles sin gastar batería.",
      fr: "Création d'un jeu fluide sans canvas maintenant 60 images par seconde sur mobile sans vider la batterie.",
      de: "Entwicklung eines interaktiven, canvas-freien Spielelements, das stabile 60 fps auf Mobilgeräten ohne Akkubelastung garantiert.",
      hi: "बैटरी समाप्त किए बिना मोबाइल फ्रेम दरों को 60 एफपीएस पर ठोस रखने वाला गेमप्ले तत्व बनाना।",
      zh: "设计了一款纯粹基于 DOM-SVG 矩阵逻辑的高帧率微型迷路探索模块，无需大型 WebGL 支撑也能保持极爽滑体验。",
      ja: "重いCanvas要素に頼らない革新的SVG物理計算で、スマートフォンのバッテリーを消耗させることなくハイパフォーマンス60fpsプレイを実現。",
      ar: "تصميم تجربة ترفيهية خفيفة للغاية تحافظ على معدل إطارات ثابت 60 إطارًا بالثانية دون استنزاف البطارية."
    },
    challenge: {
      en: "Arcade Labs suffered severe user churn when visitors encountered loading queue wait-times before their cloud multiplayers launched. Ordinary HTML wrappers bored users, causing high dropout metrics and lost transactions.",
      es: "Arcade Labs sufría altas tasas de abandono en las colas de carga. Los banners estáticos aburrían a los usuarios.",
      fr: "Arcade Labs présentait un fort taux de rebond lors du chargement des sessions. Les bannières passives ennuyaient les visiteurs.",
      de: "Arcade Labs hatte mit hoher Nutzerabwanderung in Warteschlangen zu kämpfen, da herkömmliche Ladebildschirme langweilig waren.",
      hi: "आर्केड लैब्स को उपयोगकर्ताओं के कतार रोस्टर में प्रतीक्षा करने के दौरान उच्च उछाल दर का सामना करना पड़ रहा था।",
      zh: "Arcade Labs 运营的云端大型多人竞技游戏在高峰期常有几分钟的载入排队排满状况，呆板的传统等待条导致用户极易流失和付费率暴跌。",
      ja: "Arcade Labs社では、クラウド対戦ゲームのペアリング完了を待つ数分間の「ロードキュー画面」で多くのプレイヤーが離脱してしまうという重大なビジネス上の課題を抱えていました。",
      ar: "عانت Arcade Labs من تسرب المستخدمين خلال فترات انتظار تحميل الألعاب الجماعية."
    },
    solution: {
      en: "We developed Arrow Escape: an authentic arcade labyrinth. Powered by pure SVG math coordinates, custom responsive matrix controls, and pre-cached lightweight neon physics lines avoiding expensive Canvas paint cycles.",
      es: "Desarrollamos Arrow Escape: un laberinto arcade con física pura de vectores SVG de bajo consumo.",
      fr: "Développement d'Arrow Escape : un labyrinthe d'arcade avec gestion de vecteurs SVG et physique ultra-sobre.",
      de: "Wir implementierten Arrow Escape: Ein echtes Vektor-Labyrinth mit reiner SVG-Mathematik und leuchtendem Neon-Look.",
      hi: "हमने एरो एस्केप विकसित किया: शुद्ध एसवीजी गणित निर्देशांक पर आधारित एमुलेटर जो मुख्य थ्रेड को मुक्त रखता है।",
      zh: "我们精雕细琢了 Arrow Escape 微迷宫竞技。由纯净的 SVG 多边形弹性碰撞算法、阻尼反馈计算和硬件层级光流滤镜组成，从源头上消除了繁重的 Canvas 占用。",
      ja: "そこでArrow Escapeをロード画面に組込。余計なCanvas描画サイクルを排除し、滑らかなSVG座標トラッキングとネオン調物理エフェクトをあしらったインゲームミニマリズムを実現しました。",
      ar: "قمنا بتطوير Arrow Escape: متاهة أركيد كلاسيكية قائمة بالكامل على تنسيق التموضع الرياضي المتجه."
    },
    outcome: {
      en: "Achieved a outstanding +42% growth in session retention metrics. Dropout rates dropped below 3% while waiting, transforming a negative load gap into an interactive retention center.",
      es: "Logró un crecimiento del +42% en métricas de retención de sesiones. La tasa de abandono en colas bajó del 30% a menos del 3%.",
      fr: "Hausse de +42% de la rétention d'audience. Le taux d'abandon lors du chargement est descendu sous les 3%.",
      de: "Verlängerung der Verweildauer um +42%. Die Abbrecherquote sank während der Wartezeit auf unter 3%.",
      hi: "सत्र प्रतिधारण मेट्रिक्स में +42% की वृद्धि हासिल की। इसके अलावा परित्याग दर 3% से कम हो गई।",
      zh: "实现了排队等待期间的用户留存暴涨 42%。加载排队流失率奇迹般从原来的百分之三十多跌至 3% 以下，变废为宝，将无聊的加载缺口转变成高互动的玩家温床。",
      ja: "ロード中セッション維持時間が +42% 向上。離脱率は3%未満に急落し、ストレスだった待ち時間をプレミアムな顧客接点の機会に転生させました。",
      ar: "زيادة استثنائية في حفظ بقاء الجلسة بنسبة 42% مع تراجع نسبة التجاهل."
    },
    featuredImage: "https://images.unsplash.com/photo-1550745165-9bc0b252726f?auto=format&fit=crop&w=800&q=80"
  }
];
